import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, MagnifyingGlass, Scales, TreeStructure } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { AbaRelatorio } from './relatorio/AbaRelatorio';
import { useAuditoriaInteligente } from '../../../../hooks/useAuditoriaInteligente';
import { IEmpresaOption } from '../../../../hooks/useContaCilImportar';
import {
  IBalanceteConsolidado,
  IContabil,
  IContabilBalancete,
} from '../../auditoriaInteligente/types';

import { IComposicaoLados } from './conta/papelTrabalho';
import { COMPOSICAO_CACHE_MS, COMPOSICAO_FRESCA_MS, chaveComposicaoLados } from './lados';
import { EmpresaPicker } from '../conciliacao-contabil/components/EmpresaPicker';
import { useAplicarEmpresaParam } from '../conciliacao-contabil/useEmpresaParam';
import { BalanceteArvore } from './BalanceteArvore';
import { EmpresasCards } from './EmpresasCards';
import FaixaComposicao from './FaixaComposicao';
import { GrupoEmpresasBar } from './GrupoEmpresasBar';
import {
  IGrupoEmpresas,
  alternarSelecao,
  estaConsolidado,
  paramEmpresas,
  selecaoInicial,
} from './grupoEmpresas';
import {
  COR_SELO,
  fmtMoney,
  contarReciprocas,
  contarInvertidas,
  codigoParaPapel,
  filtrarContas,
  filtrarInvertidas,
  filtrarReciprocas,
  papelDeTrabalhoUrl,
  resumirBalancete,
  seloDoBalancete,
} from './balancete';
import { Competencia, MESES, periodoDaCompetencia } from './competencia';
import {
  COR_STATUS,
  EMPRESAS_POR_PAGINA,
  IBalanceteVisaoGeral,
  IEmpresaVisaoGeral,
  ROTULO_CHIP,
  EXPLICA_STATUS,
  STATUS_ORDEM,
  StatusEmpresa,
  filtrarPorStatus,
  filtrarPorTexto,
  ordenarPorRelevancia,
  paginarEmpresas,
  totalPaginas,
} from './visaoGeral';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  brand: '#1B88F5',
  pos: '#16A34A',
  warn: '#D97706',
} as const;

const VOLTAR = '/modulesHome/accountingConciliation';

/** Anos disponíveis no seletor: o ano corrente e os 5 anteriores. */
const anosDisponiveis = (): number[] => {
  const atual = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => atual - i);
};

/**
 * Período enviado ao backend (`YYYY-MM-DD`). Ano civil completo continua sendo
 * uma opção — `periodoDaCompetencia(ano, null)` devolve exatamente o par que
 * esta tela mandava antes de 18/09/2026, quando só havia recorte anual.
 */
const periodoDoAno = (ano: number, mes: Competencia) => periodoDaCompetencia(ano, mes);

/** Meses com movimento na DRE — alimenta o "N meses" do resumo do cabeçalho. */
const mesesComDados = (data: IContabil | undefined): number =>
  (data?.dre_mensal ?? []).filter(m => (m.receita_bruta ?? 0) !== 0).length;

/**
 * Por que o filtro de recíprocas existe. Fica fora do JSX porque é texto de
 * norma, não de layout: o contador lê isso para decidir se confia no total.
 */
const EXPLICA_RECIPROCA =
  'A ITG 2000 (R1), item 24, manda eliminar as contas recíprocas entre matriz ' +
  'e filiais ao elaborar as demonstrações. A soma desta tela NÃO elimina: o ' +
  'filtro mostra quais contas, de quais empresas, continuam dentro do total.';

export function BalanceteContabilPage() {
  const navigate = useNavigate();
  const {
    getContabil,
    getBalanceteConsolidado,
    getBalanceteVisaoGeral,
    getGrupoEmpresas,
    getComposicaoLados,
  } = useAuditoriaInteligente();

  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  // Atalho `?empresa=<id>` vindo de outras telas do módulo Contábil.
  useAplicarEmpresaParam(setEmpresa);

  const [ano, setAno] = useState<number>(() => new Date().getFullYear());
  // `null` = ano inteiro (o comportamento que existia antes do filtro mensal).
  const [mes, setMes] = useState<Competencia>(null);
  // Filtro de reciprocas: desligado por padrao. Ligar ESCONDE o resto do
  // balancete, entao nunca pode vir ligado — o contador abre a tela para
  // ver o balancete, nao a excecao dele.
  const [soReciprocas, setSoReciprocas] = useState(false);
  // Filtro de saldo invertido: mesma regra do de reciprocas (desligado por
  // padrao, porque ligar esconde o resto do balancete). E' o destino do
  // clique em "N saldos invertidos" no card da empresa.
  const [soInvertidas, setSoInvertidas] = useState(false);
  // Aba ativa. 'saldos' e' o balancete (o que a tela sempre foi); 'relatorio'
  // e' o painel agregado do lote. Comeca em 'saldos' de proposito: quem abre
  // a tela quer ver a conta, nao o agregado.
  const [aba, setAba] = useState<'saldos' | 'relatorio'>('saldos');
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  // Recorte dos CARDS: chip de status + página (10 por página, no cliente).
  const [statusFiltro, setStatusFiltro] = useState<StatusEmpresa | null>(null);
  const [paginaCards, setPaginaCards] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput.trim()), 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const periodo = useMemo(() => periodoDoAno(ano, mes), [ano, mes]);

  /**
   * O balancete da empresa no período.
   *
   * `gcTime` longo pelo mesmo motivo da faixa: trocar de mês (ou de empresa) e
   * voltar é o caminho normal de quem confere, e o padrão de 5 min do React
   * Query descartava o período anterior — a mesma causa que deixava a
   * Conferência de Importações com a tela vazia na volta. Sem `staleTime`: o
   * balancete muda com a importação, então ele é sempre reconferido; guardado,
   * a tela mostra o número antigo enquanto isso em vez de um vazio.
   */
  const { data, isFetching } = useQuery<IContabil>({
    queryKey: ['balancete-contabil', { id: empresa?.value, ...periodo }],
    queryFn: getContabil,
    enabled: !!empresa,
    gcTime: COMPOSICAO_CACHE_MS,
  });

  /**
   * Indicadores da composição (a receber × a pagar).
   *
   * SEGUE O ANO DO FILTRO (corrigido em 14/10/2026). Antes a `queryKey` tinha
   * só a empresa, então trocar a data não refazia nada e a faixa repetia os
   * mesmos valores — foi o defeito relatado: "mesmo que seja para ano anterior,
   * ele ainda continua apresentando os mesmos valores".
   *
   * O ano vai como `exercicio` porque o lote grava só o exercício corrente
   * (medido: as 526 empresas com `data_ini = 01/01/2026`, nenhuma fotografia de
   * 2025). Pedindo o ano corrente, o backend lê o lote e abre em
   * milissegundos; pedindo outro, ele calcula na hora — daí o `staleTime`
   * maior para não recalcular a cada volta à tela.
   */
  const { data: lados, isFetching: carregandoLados } = useQuery<IComposicaoLados>({
    queryKey: chaveComposicaoLados(empresa?.value, ano),
    queryFn: getComposicaoLados,
    enabled: !!empresa,
    staleTime: COMPOSICAO_FRESCA_MS,
    gcTime: COMPOSICAO_CACHE_MS,
  });

  /**
   * Grupo matriz/filiais da empresa selecionada — uma chamada por seleção,
   * guardada por 10 min (o cadastro só muda na importação semanal). Diz ao
   * contador onde ele está e deixa trocar de estabelecimento sem voltar aos
   * cards. NÃO consolida números: ver `grupoEmpresas.ts`.
   */
  const { data: grupo } = useQuery<IGrupoEmpresas | null>({
    queryKey: ['grupo-empresas', empresa?.value],
    queryFn: getGrupoEmpresas,
    enabled: !!empresa,
    staleTime: 10 * 60 * 1000,
  });

  /**
   * Visão geral da carteira — é ela que abre a tela. Só é buscada enquanto
   * NENHUMA empresa está selecionada: escolhida a empresa, os cards saem de
   * cena e manter a query viva custaria uma varredura da carteira inteira a
   * cada troca de ano.
   */
  const { data: visaoGeral, isFetching: carregandoCards } = useQuery<IBalanceteVisaoGeral>({
    queryKey: ['balancete-visao-geral', { ...periodo }],
    queryFn: getBalanceteVisaoGeral,
    enabled: !empresa,
  });

  /**
   * A busca é FILTRO dos cards, não porta de entrada. Aplicada no cliente
   * (como o status e a paginação) porque mandá-la ao servidor recortaria a
   * lista sem recompor o `resumo` — os chips passariam a contar o filtro em
   * vez da carteira.
   */
  // Ordena por relevância de conferência (apontamentos, depois movimento)
  // DEPOIS de filtrar: ordenar antes gastaria trabalho no que o filtro descarta.
  const empresasFiltradas = useMemo(
    () =>
      ordenarPorRelevancia(
        filtrarPorStatus(filtrarPorTexto(visaoGeral?.itens ?? [], busca), statusFiltro),
      ),
    [visaoGeral, busca, statusFiltro],
  );

  const paginasCards = totalPaginas(empresasFiltradas.length, EMPRESAS_POR_PAGINA);
  // Grampeia a página ao recorte corrente: filtrar estando na página 7 tem que
  // cair na última existente, nunca numa tela em branco.
  const paginaCorrente = Math.min(Math.max(1, paginaCards), paginasCards);
  const cardsDaPagina = useMemo(
    () => paginarEmpresas(empresasFiltradas, paginaCorrente, EMPRESAS_POR_PAGINA),
    [empresasFiltradas, paginaCorrente],
  );

  /** Clicar no card = selecionar a empresa (mesmíssimo efeito da busca). */
  const abrirEmpresa = (e: IEmpresaVisaoGeral) => {
    setEmpresa({
      value: e.empresa_id,
      label: e.nome,
      external_id: String(e.external_id ?? ''),
    });
    setBuscaInput('');
    setBusca('');
  };

  /** Alternar chip: clicar no que já está ativo limpa o filtro. */
  const alternarStatus = (s: StatusEmpresa) => {
    setStatusFiltro(atual => (atual === s ? null : s));
    setPaginaCards(1);
  };

  // SELECAO PARA SOMAR (pedido do usuario, 01/10/2026). Comeca so' com a
  // empresa aberta: o numero somado e' diferente do balancete de uma empresa,
  // e ninguem deve receber um total que nao pediu.
  const [selecionados, setSelecionados] = useState<number[]>([]);
  // A dependencia e' o ID da empresa consultada, NUNCA o objeto `grupo`.
  //
  // `grupo` vem do react-query: a cada refetch (foco da janela, invalidacao,
  // `staleTime` vencido) ele volta como um objeto NOVO com o mesmo conteudo.
  // Com `[grupo]` o efeito disparava nesse refetch e chamava `selecaoInicial`,
  // JOGANDO FORA a selecao do usuario — marcar a filial voltava sozinho para
  // so' a empresa aberta. Em producao (856+970) o efeito era pior: a tela
  // passava a mostrar so' a filial, porque o reset competia com o clique.
  //
  // O id so' muda quando o contador realmente troca de empresa — que e'
  // exatamente quando a selecao DEVE recomecar.
  const consultadaId = grupo?.consultada_membro_id ?? null;
  useEffect(() => {
    setSelecionados(grupo ? selecaoInicial(grupo) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultadaId]);
  const consolidado = estaConsolidado(selecionados);

  const { data: dataConsolidado } = useQuery<IBalanceteConsolidado>({
    queryKey: [
      'balancete-consolidado',
      { id: empresa?.value, ...periodo, empresas: paramEmpresas(selecionados) },
    ],
    queryFn: getBalanceteConsolidado,
    enabled: !!empresa && consolidado,
  });

  // Com mais de um marcado a arvore mostra a SOMA; com um so', o balancete da
  // empresa aberta. Fonte unica para a arvore E o resumo do cabecalho, para os
  // dois nunca contarem coisas diferentes.
  const contas = useMemo(
    () => (consolidado ? dataConsolidado?.balancete : data?.balancete) ?? [],
    [consolidado, data, dataConsolidado],
  );
  const resumo = useMemo(() => resumirBalancete(contas), [contas]);
  const selo = useMemo(
    () => seloDoBalancete(resumo, data?.balancete_cargas ?? null),
    [resumo, data],
  );
  // Quantas linhas analiticas tem reciproca — e' o numero do botao. Conta
  // sobre `contas` (tudo), nao sobre o recorte, senao o proprio filtro
  // mudaria o numero que ele promete mostrar.
  const qtdReciprocas = useMemo(() => contarReciprocas(contas), [contas]);
  // Mesmo criterio do numero de reciprocas: conta sobre `contas` (tudo), para
  // o filtro nao mudar o numero que ele promete mostrar.
  const qtdInvertidas = useMemo(() => contarInvertidas(contas), [contas]);
  const filtradas = useMemo(() => {
    // Os dois filtros COMPOEM: ligar ambos mostra a intersecao (conta que e'
    // reciproca E esta' invertida), nao o ultimo clicado.
    let base = soReciprocas ? filtrarReciprocas(contas) : contas;
    if (soInvertidas) base = filtrarInvertidas(base);
    return filtrarContas(base, busca);
  }, [contas, busca, soReciprocas, soInvertidas]);

  // Desliga o filtro quando o recorte novo NAO tem reciproca — trocar de
  // empresa, de periodo ou desmarcar a filial deixaria a arvore vazia, e o
  // usuario nao teria como saber que o culpado era um filtro ainda ligado.
  // De proposito NAO desliga quando o novo recorte tambem tem reciproca: ai
  // ele continua vendo o que pediu para ver.
  useEffect(() => {
    if (qtdReciprocas === 0) setSoReciprocas(false);
  }, [qtdReciprocas]);
  // Mesma protecao para o filtro de invertidas: sem ela, trocar de empresa
  // com o filtro ligado deixaria a arvore vazia sem dizer por que.
  //
  // Protege tambem o clique vindo da ressalva do Relatorio, porque as duas
  // regras NAO sao a mesma (medido no backend em 23/10/2026):
  //   - o `alerta` do balancete cobre os grupos 1 a 4 (inclui receita e
  //     despesa) e olha o saldo do PERIODO filtrado;
  //   - o `saldos_invertidos` do relatorio cobre so' 1 e 2, sobre o
  //     `saldo_razao` da fotografia do lote.
  // Uma conta de receita invertida aparece na ressalva e NAO no filtro da
  // arvore; nesse caso o filtro se desliga e o contador ve o balancete
  // inteiro da empresa, em vez de uma arvore vazia.
  useEffect(() => {
    if (qtdInvertidas === 0) setSoInvertidas(false);
  }, [qtdInvertidas]);
  const meses = mesesComDados(data);

  /**
   * Na aba de saldos, a arvore ou o estado vazio. Variavel nomeada em vez de
   * ternario aninhado, que a regra `no-nested-ternary` proibe — e que aqui
   * esconderia que o "vazio" vale SO' para a empresa aberta: a carteira pode
   * ter dado mesmo quando essa empresa nao tem.
   */
  const corpo: 'vazio' | 'arvore' = contas.length === 0 && !isFetching ? 'vazio' : 'arvore';

  /**
   * Qual das TRES telas de topo aparece. Nomeada em vez de ternario aninhado
   * (regra `no-nested-ternary`):
   *
   *   'relatorio' -> o painel agregado, com ou sem empresa aberta;
   *   'carteira'  -> os cards de empresa, quando nenhuma foi escolhida;
   *   'empresa'   -> o balancete da empresa aberta.
   *
   * O relatorio vem PRIMEIRO de proposito: ele funciona sem empresa
   * selecionada, e amarra-lo ao `!empresa` o tornaria inacessivel justamente
   * no modo de carteira inteira, que e' um dos dois que o usuario pediu.
   */
  const tela: 'relatorio' | 'carteira' | 'empresa' = (() => {
    if (aba === 'relatorio') return 'relatorio';
    return empresa ? 'empresa' : 'carteira';
  })();

  // Porta de entrada da Composição de Saldo: a conta analítica clicada abre o
  // Papel de trabalho dela (tela seguinte). Ver contrato em `papelDeTrabalhoUrl`.
  /**
   * Abre o papel de trabalho de uma conta da lista do "Ver detalhes".
   *
   * A lista já traz `classificacao` do backend, então não depende do mapa de
   * códigos da árvore — que é por empresa e some no consolidado.
   */
  const abrirContaDaLista = (codigoConta: string) => {
    if (!empresa) return;
    const c = (lados?.contas_detalhe ?? []).find(x => x.conta === codigoConta);
    navigate(
      papelDeTrabalhoUrl({
        empresaId: empresa.value,
        classificacao: c?.classificacao ?? '',
        codigoConta,
        dataIni: periodo.data_ini,
        dataFim: periodo.data_fim,
      }),
    );
  };
  const abrirPapelDeTrabalho = (conta: IContabilBalancete) => {
    if (!empresa) return;
    // No balancete SOMADO a linha vem sem `codigo_conta` — a mesma conta tem
    // código diferente em cada empresa. `codigoParaPapel` pega o código da
    // empresa ABERTA no mapa `codigos`. Medido: as 175 analíticas do
    // consolidado 856+970 vinham sem código, e o papel abria vazio com
    // "null — 2026" no cabeçalho.
    const codigo = codigoParaPapel(conta, Number(empresa.external_id));
    // Sem código não navega: abrir o papel da conta errada é pior que não
    // abrir. Acontece quando a linha somada não tem a conta na empresa aberta.
    if (!codigo) return;
    navigate(
      papelDeTrabalhoUrl({
        empresaId: empresa.value,
        classificacao: conta.classificacao,
        codigoConta: codigo,
        dataIni: periodo.data_ini,
        dataFim: periodo.data_fim,
      }),
    );
  };

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={5}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color={COR.muted}
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: COR.ink }}
              onClick={() => navigate(VOLTAR)}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bgGradient="linear(135deg, #E0E7FF 0%, #DDD6FE 100%)"
              color="#6366F1"
              boxShadow="0 8px 24px -6px rgba(99, 102, 241, 0.4)"
              flexShrink={0}
            >
              <Scales size={32} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color={COR.faint}
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color={COR.ink}>
                Composição de Saldo
              </Heading>
              <Text fontSize="sm" color={COR.muted} mt={0.5}>
                {empresa
                  ? 'O saldo de cada conta na ordem do plano de contas. Clique numa conta analítica para abrir o papel de trabalho dela.'
                  : 'Escolha uma empresa da carteira para ver a composição de saldo dela.'}
              </Text>
            </Box>
          </Flex>
        </motion.div>

        {/* Barra de contexto: empresa + ano + busca */}
        <GlassCard intensity="medium" p={4} mb={5}>
          <Flex gap={3} wrap="wrap" align={{ md: 'flex-end' }}>
            <Box flex={1} minW="260px">
              <EmpresaPicker value={empresa} onChange={setEmpresa} />
            </Box>
            <Box minW="110px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color={COR.muted}
                mb={1}
                textTransform="uppercase"
              >
                Ano
              </Text>
              <Select
                size="sm"
                borderRadius="lg"
                bg="white"
                value={ano}
                onChange={e => setAno(Number(e.target.value))}
                aria-label="Ano da composição de saldo"
              >
                {anosDisponiveis().map(a => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </Box>
            {/*
              Competência: recorta o período por MÊS. "Ano inteiro" continua na
              lista (é o default) — quem concilia o exercício fechado não perde
              a leitura que já tinha.
            */}
            <Box minW="150px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color={COR.muted}
                mb={1}
                textTransform="uppercase"
              >
                Competência
              </Text>
              <Select
                size="sm"
                borderRadius="lg"
                bg="white"
                value={mes ?? ''}
                onChange={e => setMes(e.target.value === '' ? null : Number(e.target.value))}
                aria-label="Competência da composição de saldo"
              >
                <option value="">Ano inteiro</option>
                {MESES.map((nome, i) => (
                  <option key={nome} value={i + 1}>
                    {nome}
                  </option>
                ))}
              </Select>
            </Box>
            <Box flex={1} minW="240px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color={COR.muted}
                mb={1}
                textTransform="uppercase"
              >
                {empresa ? 'Filtrar conta' : 'Filtrar empresa'}
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={14} color="#94A3B8" />
                </InputLeftElement>
                {/* A busca deixou de ser porta de entrada: sem empresa ela
                    filtra os CARDS; com empresa, as contas. Nunca fica
                    desabilitada — era isso que fazia a tela abrir inerte. */}
                <Input
                  borderRadius="lg"
                  bg="white"
                  placeholder={
                    empresa ? 'Código, nome ou classificação' : 'Nome, id ou CNPJ da empresa'
                  }
                  value={buscaInput}
                  onChange={e => {
                    setBuscaInput(e.target.value);
                    setPaginaCards(1);
                  }}
                />
              </InputGroup>
            </Box>
          </Flex>
        </GlassCard>

        {/* ── Abas: saldos por conta x relatorio agregado ──
            FORA do ramo `!empresa` de proposito: o relatorio de CARTEIRA
            INTEIRA nao precisa de empresa selecionada, e e' um dos dois modos
            que o usuario pediu. Dentro do ramo, a aba ficaria inacessivel
            justamente no modo mais util. */}
        <Flex
          borderWidth="1px"
          borderColor="rgba(27, 136, 245, 0.35)"
          borderRadius="lg"
          overflow="hidden"
          w="fit-content"
          mb={4}
        >
          {(
            [
              ['saldos', 'Saldos por conta'],
              ['relatorio', 'Relatório'],
            ] as ['saldos' | 'relatorio', string][]
          ).map(([id, rotulo]) => (
            <Box
              key={id}
              as="button"
              px={4}
              py={2}
              fontSize="13px"
              fontWeight="700"
              bg={aba === id ? COR.brand : 'white'}
              color={aba === id ? 'white' : COR.brand}
              _hover={{ bg: aba === id ? '#1573D1' : 'rgba(27, 136, 245, 0.08)' }}
              aria-pressed={aba === id}
              onClick={() => setAba(id)}
              {...{ type: 'button' }}
            >
              {rotulo}
            </Box>
          ))}
        </Flex>

        {tela === 'relatorio' && (
          /* O recorte sao as empresas MARCADAS na barra do grupo. Nenhuma
             marcada = carteira inteira, e nesse modo a rota exige perfil
             tecnico (Admin/Desenvolvimento). */
          <AbaRelatorio
            empresas={selecionados}
            ativa
            /* Clique numa conta invertida da ressalva: abre a empresa dela na
               aba de saldos, com o filtro de invertidas ligado.
               Sem `empresa_id` NAO navega: o balancete e' consultado por esse
               id, e abrir sem ele mostraria a arvore vazia — pior que nao
               abrir, porque parece que a empresa nao tem conta invertida. */
            onAbrirInvertida={l => {
              if (!l.empresa_id) return;
              setSoInvertidas(true);
              setAba('saldos');
              // O `detalhe` do relatorio NAO traz o nome da empresa, e o
              // cabecalho da tela exibe `label`: sem procurar o nome na visao
              // geral, o titulo viraria "856" em vez da razao social. O id
              // fica so' como ultimo recurso (carteira ainda carregando).
              const naCarteira = (visaoGeral?.itens ?? []).find(e => e.empresa_id === l.empresa_id);
              setEmpresa({
                value: l.empresa_id,
                label: naCarteira?.nome ?? String(l.external_id),
                external_id: String(l.external_id ?? ''),
              });
              setBuscaInput('');
              setBusca('');
            }}
          />
        )}
        {tela === 'carteira' && (
          <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
            {/* ── Chips de status: contagem da CARTEIRA INTEIRA ──
                Vêm de `resumo`, que o backend calcula sobre todas as empresas.
                Por isso o filtro de status é aplicado no cliente: recortar no
                servidor faria os chips contarem o próprio filtro. */}
            {visaoGeral && (
              <Flex align="center" gap={2} wrap="wrap" mb={4}>
                <Text fontSize="12px" fontWeight="700" color={COR.ink}>
                  {visaoGeral.total} {visaoGeral.total === 1 ? 'empresa' : 'empresas'} na carteira
                </Text>
                <Box w="1px" h="14px" bg="rgba(15, 23, 42, 0.12)" mx={1} />
                {STATUS_ORDEM.map(s => {
                  const qtd = visaoGeral.resumo[s] ?? 0;
                  const ativo = statusFiltro === s;
                  const cor = COR_STATUS[s];
                  return (
                    <Badge
                      key={s}
                      as="button"
                      borderRadius="full"
                      px={2.5}
                      py={1}
                      fontSize="11px"
                      fontWeight="700"
                      textTransform="none"
                      bg={ativo ? cor.fg : cor.bg}
                      color={ativo ? 'white' : cor.fg}
                      border="1px solid"
                      borderColor={cor.borda}
                      opacity={qtd === 0 && !ativo ? 0.45 : 1}
                      title={EXPLICA_STATUS[s]}
                      aria-pressed={ativo}
                      onClick={() => alternarStatus(s)}
                      {...{ type: 'button' }}
                    >
                      {ROTULO_CHIP[s]} {qtd}
                    </Badge>
                  );
                })}
                {(statusFiltro || busca) && (
                  <Text fontSize="11px" color={COR.muted}>
                    · {empresasFiltradas.length} no recorte
                  </Text>
                )}
              </Flex>
            )}

            {carregandoCards && !visaoGeral ? (
              <Flex direction="column" align="center" gap={3} py={14}>
                <Spinner size="sm" color={COR.brand} />
                <Text fontSize="sm" color={COR.faint}>
                  Carregando as empresas da carteira...
                </Text>
              </Flex>
            ) : null}

            {visaoGeral && empresasFiltradas.length === 0 ? (
              <Flex direction="column" align="center" gap={3} py={14}>
                <TreeStructure size={40} weight="duotone" color="#CBD5E1" />
                <Text fontSize="sm" color={COR.faint} textAlign="center">
                  Nenhuma empresa da carteira bate com este recorte.
                </Text>
              </Flex>
            ) : null}

            {visaoGeral && empresasFiltradas.length > 0 && (
              <EmpresasCards
                empresas={cardsDaPagina}
                onAbrir={abrirEmpresa}
                pagina={paginaCorrente}
                paginas={paginasCards}
                totalFiltrado={empresasFiltradas.length}
                onPagina={setPaginaCards}
                carregando={carregandoCards}
              />
            )}
          </GlassCard>
        )}
        {/* `empresa &&` e' redundante com `tela === 'empresa'`, mas devolve
            ao TypeScript o estreitamento de tipo que a variavel derivada
            perde — sem ele, `empresa.label` acusa possivel null. */}
        {tela === 'empresa' && empresa && (
          <>
            {/* Resumo do estado da base — no espírito do cabeçalho aprovado:
                "ECD 2025 · 240 contas · 12 meses · abertura ✓ fecha". */}
            <GlassCard intensity="medium" p={4} mb={5}>
              <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                {/* `flex="1 1 260px"` + `minW={0}` é o par que faz o
                    `isTruncated` funcionar: sem a base flexível a caixa cresce
                    junto com o label da empresa (`856 · RAZÃO · CNPJ`), empurra
                    os totais para fora do card e o texto nunca chega a cortar. */}
                <Box flex="1 1 260px" minW={0}>
                  <Text fontSize="md" fontWeight="700" color={COR.ink} isTruncated>
                    {empresa.label}
                  </Text>
                  <Flex align="center" gap={1.5} mt={1} wrap="wrap" fontSize="12px">
                    <Text color={COR.muted} fontWeight="600">
                      ECD {ano}
                    </Text>
                    <Text color={COR.faint}>·</Text>
                    <Text color={COR.muted}>{resumo.totalContas} contas</Text>
                    {/* Só dizemos "N analíticas" quando há sintética para
                        contrastar. Em empresa cujo extrato vem todo analítico
                        (79 de 79 na 856), "79 contas · 79 analíticas" repete o
                        mesmo número duas vezes e lê como defeito. */}
                    {resumo.sinteticas > 0 && (
                      <>
                        <Text color={COR.faint}>·</Text>
                        <Text color={COR.muted}>{resumo.analiticas} analíticas</Text>
                      </>
                    )}
                    {/* `meses` vem da DRE, que É filtrada pelo período; o
                        balancete NÃO é (extract_balancete não tem coluna de
                        data). Sem movimento na DRE do ano, "0 meses" ao lado de
                        contas com saldo parece dado quebrado — melhor omitir. */}
                    {meses > 0 && (
                      <>
                        <Text color={COR.faint}>·</Text>
                        <Text color={COR.muted}>{meses} meses</Text>
                      </>
                    )}
                    {resumo.alertas > 0 && (
                      <>
                        <Text color={COR.faint}>·</Text>
                        {/* Atalho pedido pela operação (23/10/2026): o número
                            era só texto e o contador tinha de abrir conta por
                            conta. Aqui a empresa JÁ está aberta, então o
                            clique só liga o filtro da árvore e leva à aba de
                            saldos, onde as contas aparecem. */}
                        <Text
                          as="button"
                          type="button"
                          color={COR.warn}
                          fontWeight="600"
                          textDecoration="underline"
                          textUnderlineOffset="2px"
                          _hover={{ color: '#B45309' }}
                          onClick={() => {
                            setSoInvertidas(true);
                            setAba('saldos');
                          }}
                          title="Ver só as contas com saldo invertido"
                        >
                          {resumo.alertas === 1
                            ? '1 saldo invertido'
                            : `${resumo.alertas} saldos invertidos`}
                        </Text>
                      </>
                    )}
                  </Flex>
                  {/* Matriz/filiais da empresa: onde o contador está e para
                      onde pode ir. Muda só quando há grupo (matriz sozinha é
                      o caso normal e fica muda). */}
                  {grupo && (
                    <GrupoEmpresasBar
                      grupo={grupo}
                      onTrocar={setEmpresa}
                      selecionados={selecionados}
                      onAlternar={id => setSelecionados(atual => alternarSelecao(atual, id, grupo))}
                    />
                  )}
                  {/* AVISOS DA SOMA: obrigatorios. Sem eles o contador le' a soma
                      simples como consolidado contabil — a folha de uma filial
                      pode estar lancada nos dois livros e as contas intercompany
                      continuam no total. */}
                  {consolidado &&
                    (dataConsolidado?.avisos ?? []).map(aviso => (
                      <Flex
                        key={aviso}
                        mt={2}
                        align="flex-start"
                        gap={2}
                        bg="rgba(217, 119, 6, 0.08)"
                        border="1px solid rgba(217, 119, 6, 0.25)"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        maxW="680px"
                      >
                        <Text fontSize="12px" color={COR.muted}>
                          {aviso}
                        </Text>
                      </Flex>
                    ))}
                </Box>

                {resumo.totalContas > 0 && (
                  // `flexShrink={0}` protege os totais: em empresa de porte
                  // (18,9 mi na 856) o valor formatado é largo e, podendo
                  // encolher, quebrava no meio do número.
                  <Flex align="center" gap={4} wrap="wrap" flexShrink={0}>
                    <Box textAlign="right">
                      <Text
                        fontSize="9px"
                        fontWeight="700"
                        color={COR.faint}
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Total débito
                      </Text>
                      <Text
                        fontSize="13px"
                        fontWeight="700"
                        color={COR.ink}
                        whiteSpace="nowrap"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmtMoney(resumo.totalDebito)}
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Text
                        fontSize="9px"
                        fontWeight="700"
                        color={COR.faint}
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                      >
                        Total crédito
                      </Text>
                      <Text
                        fontSize="13px"
                        fontWeight="700"
                        color={COR.ink}
                        whiteSpace="nowrap"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {fmtMoney(resumo.totalCredito)}
                      </Text>
                    </Box>
                    {/* O selo confere DOIS eixos: débitos = créditos (partida
                        dobrada) E cada saldo = anterior + débito − crédito. Verde
                        só quando os dois passam; se só o primeiro passa, fica
                        âmbar e diz quantos saldos divergem e por quê (datas das
                        duas cargas) — ver `seloDoBalancete`. */}
                    <Badge
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="11px"
                      fontWeight="700"
                      textTransform="none"
                      bg={COR_SELO[selo.tom].bg}
                      color={COR_SELO[selo.tom].fg}
                      title={selo.explicacao}
                    >
                      {selo.rotulo}
                    </Badge>
                  </Flex>
                )}
              </Flex>
            </GlassCard>

            {/*
              A faixa da composição. NÃO repete o TOTAL DÉBITO/CRÉDITO acima:
              aquele par soma os dois lados do MESMO lançamento e sempre fecha
              (medido: 60/60 empresas, e nenhum dos 673.930 lançamentos do ano
              tem lado nulo). Esta faixa mede conciliação, que é outra coisa.
            */}
            <FaixaComposicao
              contas={lados?.contas_detalhe}
              onAbrirConta={abrirContaDaLista}
              lados={lados?.lados}
              total={lados?.total}
              aviso={lados?.aviso}
              carregando={carregandoLados}
              exercicio={lados?.exercicio}
              origem={lados?.origem}
              parcial={lados?.parcial}
            />

            {corpo === 'vazio' && (
              <GlassCard intensity="medium" py={16}>
                <Flex direction="column" align="center" gap={3}>
                  <Scales size={40} weight="duotone" color="#CBD5E1" />
                  <Text fontSize="sm" color={COR.faint} textAlign="center">
                    Sem saldos importados para esta empresa.
                  </Text>
                </Flex>
              </GlassCard>
            )}
            {corpo === 'arvore' && (
              <>
                {/* Botao de evidencia (ITG 2000 R1, item 24): recorta a
                    arvore nas contas reciprocas, que a soma simples NAO
                    elimina. So aparece quando existe o que mostrar. */}
                {qtdReciprocas > 0 && (
                  <Flex align="center" gap={2} mb={2} wrap="wrap">
                    <Button
                      size="xs"
                      borderRadius="full"
                      fontWeight="700"
                      variant={soReciprocas ? 'solid' : 'outline'}
                      colorScheme="purple"
                      aria-pressed={soReciprocas}
                      onClick={() => setSoReciprocas(v => !v)}
                      title={EXPLICA_RECIPROCA}
                    >
                      {soReciprocas ? 'Mostrando' : 'Ver'} {qtdReciprocas}{' '}
                      {qtdReciprocas === 1 ? 'conta recíproca' : 'contas recíprocas'}
                    </Button>
                    {soReciprocas && (
                      <Text fontSize="11px" color={COR.muted}>
                        · a eliminar no consolidado (ITG 2000 R1, item 24)
                      </Text>
                    )}
                  </Flex>
                )}
                {/* Recorta a arvore nas contas com saldo contrario a natureza.
                    E' o destino do clique em "N saldos invertidos" no card —
                    antes o contador tinha de abrir conta por conta. */}
                {qtdInvertidas > 0 && (
                  <Flex align="center" gap={2} mb={2} wrap="wrap">
                    <Button
                      size="xs"
                      borderRadius="full"
                      fontWeight="700"
                      variant={soInvertidas ? 'solid' : 'outline'}
                      colorScheme="orange"
                      aria-pressed={soInvertidas}
                      onClick={() => setSoInvertidas(v => !v)}
                      title="Saldo contrário à natureza da conta: ativo credor, passivo devedor. Pede conferência."
                    >
                      {soInvertidas ? 'Mostrando' : 'Ver'} {qtdInvertidas}{' '}
                      {qtdInvertidas === 1 ? 'saldo invertido' : 'saldos invertidos'}
                    </Button>
                    {soInvertidas && (
                      <Text fontSize="11px" color={COR.muted}>
                        · saldo contrário à natureza da conta
                      </Text>
                    )}
                  </Flex>
                )}
                <BalanceteArvore
                  contas={filtradas}
                  carregando={isFetching}
                  buscaAtiva={!!busca || soReciprocas || soInvertidas}
                  onAbrirConta={abrirPapelDeTrabalho}
                />
              </>
            )}
          </>
        )}
      </Box>
    </GlassPageBg>
  );
}
